import { getAtlasApiBaseUrl, getSimulationApiBaseUrl } from '../getAtlasApiBaseUrl';
import { getToken } from '../../components/authentication/functions/authentication_management';

export interface GeoJsonLineString {
  type: 'LineString';
  coordinates: [number, number][];
}

export interface SegmentRouteMetadata {
  vehicle_shift_id?: string;
  segment_index?: number;
  from_driver_activity_number?: number;
  to_driver_activity_number?: number;
  /** Trip ids known to be onboard for this segment row. */
  rides_onboard_ids?: string[];
  /** Road distance of the leg in metres. */
  distance_m?: number;
  /** Optional upstream naming metadata used for route/fleet labels. */
  shift_band?: string;
  shift_name?: string;
  route_name?: string;
  fleet_name?: string;
  manifest_name?: string;
}

export interface RoadRouteWithMetadata {
  geometry: GeoJsonLineString | null;
  metadata?: SegmentRouteMetadata;
}

export interface ResultsPathsOptions {
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
  /** Reject on a failed or timed-out request instead of resolving to no routes. */
  throwOnError?: boolean;
}

/** Large `/paths` payloads (many rides) can exceed a short deadline on slow links. */
const DEFAULT_TIMEOUT_MS = 60000;
const API_BASE_URL = getSimulationApiBaseUrl();
const FALLBACK_API_BASE_URL = getAtlasApiBaseUrl();
const PATHS_RESPONSE_KEYS = ['paths', 'data', 'results', 'items', 'rides', 'route_paths'];
/** Prefer `points` first — simulation `/paths` returns segment polylines as lat/lng point arrays. */
const PATH_COORD_KEYS = ['points', 'path', 'paths', 'geometry', 'coordinates', 'coords'];
const RIDE_ID_KEYS = [
  'ride_id',
  'rideId',
  'driver_ride_id',
  'driverRideId',
  'vehicle_shift_id',
  'vehicleShiftId',
  'request_id',
  'requestId',
  'rider_ride_id',
  'riderRideId',
  'external_reference_id',
  'externalReferenceId',
  'id',
];

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toGeoJsonPoint(rawPoint: unknown): [number, number] | null {
  if (Array.isArray(rawPoint) && rawPoint.length >= 2) {
    const first = toNumber(rawPoint[0]);
    const second = toNumber(rawPoint[1]);
    if (first == null || second == null) return null;
    // Supports both [lat, lng] and [lng, lat].
    if (Math.abs(first) <= 90 && Math.abs(second) <= 180) return [second, first];
    if (Math.abs(first) <= 180 && Math.abs(second) <= 90) return [first, second];
    return null;
  }

  if (rawPoint && typeof rawPoint === 'object') {
    const point = rawPoint as Record<string, unknown>;
    const lat = toNumber(point.lat ?? point.latitude);
    const lng = toNumber(point.lng ?? point.lon ?? point.longitude);
    if (lat == null || lng == null) return null;
    return [lng, lat];
  }

  return null;
}

function toGeoJsonLineString(rawGeometry: unknown): GeoJsonLineString | null {
  if (!rawGeometry) return null;

  if (
    typeof rawGeometry === 'object' &&
    rawGeometry != null &&
    (rawGeometry as Record<string, unknown>).type !== 'LineString'
  ) {
    const candidateCoordinates = (rawGeometry as Record<string, unknown>).coordinates;
    if (Array.isArray(candidateCoordinates)) {
      const coordinates = candidateCoordinates
        .map((point) => toGeoJsonPoint(point))
        .filter((point): point is [number, number] => point != null);
      return coordinates.length > 1 ? { type: 'LineString', coordinates } : null;
    }
  }

  if (
    typeof rawGeometry === 'object' &&
    rawGeometry != null &&
    (rawGeometry as Record<string, unknown>).type === 'LineString'
  ) {
    const geoJsonCoordinates = (rawGeometry as Record<string, unknown>).coordinates;
    if (Array.isArray(geoJsonCoordinates)) {
      const coordinates = geoJsonCoordinates
        .map((point) => toGeoJsonPoint(point))
        .filter((point): point is [number, number] => point != null);
      return coordinates.length > 1 ? { type: 'LineString', coordinates } : null;
    }
  }

  if (Array.isArray(rawGeometry)) {
    const coordinates = rawGeometry
      .map((point) => toGeoJsonPoint(point))
      .filter((point): point is [number, number] => point != null);
    return coordinates.length > 1 ? { type: 'LineString', coordinates } : null;
  }

  return null;
}

function extractAllRideIds(item: Record<string, unknown>): string[] {
  const ids: string[] = [];
  const primary = extractPathPointGroupId(item);
  if (primary) ids.push(primary);
  const hasSegmentIndex =
    (item.segment_index !== undefined && item.segment_index !== null) ||
    (item.segmentIndex !== undefined && item.segmentIndex !== null);
  for (const key of RIDE_ID_KEYS) {
    // Avoid registering the same shift id for every leg — use `vehicle_shift_id:segment_index` only.
    if (hasSegmentIndex && (key === 'vehicle_shift_id' || key === 'vehicleShiftId')) {
      continue;
    }
    const value = item[key];
    if (typeof value === 'string' && value.trim() !== '') ids.push(value);
    else if (typeof value === 'number' && Number.isFinite(value)) ids.push(String(value));
  }
  return Array.from(new Set(ids));
}

/**
 * Flat `/results/{id}/paths` rows are keyed by driver leg. Prefer `driver_ride_id` over
 * generic `ride_id` so rows are not merged into one polyline when the API sends both fields.
 * Segment-style payloads use `vehicle_shift_id` + `segment_index` (one polyline per leg).
 */
function extractPathPointGroupId(item: Record<string, unknown>): string | null {
  const shiftRaw = item.vehicle_shift_id ?? item.vehicleShiftId;
  const segRaw = item.segment_index ?? item.segmentIndex;
  if (typeof shiftRaw === 'string' && shiftRaw.trim() !== '') {
    if (typeof segRaw === 'number' && Number.isFinite(segRaw)) {
      return `${shiftRaw}:${segRaw}`;
    }
    if (typeof segRaw === 'string' && segRaw.trim() !== '') {
      return `${shiftRaw}:${segRaw}`;
    }
    return shiftRaw;
  }

  const driverKeys = ['driver_ride_id', 'driverRideId'] as const;
  for (const key of driverKeys) {
    const value = item[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return extractRideId(item);
}

function extractRideId(item: Record<string, unknown>): string | null {
  for (const key of RIDE_ID_KEYS) {
    const value = item[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }

  const nestedRide = item.ride;
  if (nestedRide && typeof nestedRide === 'object') {
    const nestedRecord = nestedRide as Record<string, unknown>;
    for (const key of RIDE_ID_KEYS) {
      const value = nestedRecord[key];
      if (typeof value === 'string' && value.trim() !== '') return value;
      if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    }
  }

  const nestedRequest = item.request;
  if (nestedRequest && typeof nestedRequest === 'object') {
    const nestedRecord = nestedRequest as Record<string, unknown>;
    for (const key of RIDE_ID_KEYS) {
      const value = nestedRecord[key];
      if (typeof value === 'string' && value.trim() !== '') return value;
      if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    }
  }

  return null;
}

function extractGeometry(item: Record<string, unknown>): GeoJsonLineString | null {
  for (const key of PATH_COORD_KEYS) {
    const geometry = toGeoJsonLineString(item[key]);
    if (geometry != null) return geometry;
  }
  return null;
}

function extractRidesOnboardIds(item: Record<string, unknown>): string[] {
  const rides = item.rides_onboard;
  if (!Array.isArray(rides)) return [];
  const ids: string[] = [];
  for (const r of rides) {
    if (!r || typeof r !== 'object') continue;
    const rec = r as Record<string, unknown>;
    const rideId = rec.ride_id ?? rec.rideId ?? rec.rider_ride_id ?? rec.riderRideId;
    const ext = rec.external_reference_id ?? rec.externalReferenceId;
    if (typeof rideId === 'string' && rideId.trim() !== '') ids.push(rideId.trim());
    if (typeof ext === 'string' && ext.trim() !== '') ids.push(ext.trim());
  }
  return Array.from(new Set(ids));
}

function extractSegmentRouteMetadata(item: Record<string, unknown>): SegmentRouteMetadata | undefined {
  const shift = item.vehicle_shift_id ?? item.vehicleShiftId;
  const seg = toNumber(item.segment_index ?? item.segmentIndex);
  const fromAct = toNumber(item.from_driver_activity_number ?? item.fromDriverActivityNumber);
  const toAct = toNumber(item.to_driver_activity_number ?? item.toDriverActivityNumber);
  const ridesOnboard = extractRidesOnboardIds(item);
  const distance = toNumber(item.distance);
  const shiftBand = item.shift_band ?? item.shiftBand;
  const shiftName = item.shift_name ?? item.shiftName;
  const routeName = item.route_name ?? item.routeName;
  const fleetName = item.fleet_name ?? item.fleetName;
  const manifestName = item.manifest_name ?? item.manifestName;

  const hasAny =
    (typeof shift === 'string' && shift.trim() !== '') ||
    seg != null ||
    fromAct != null ||
    toAct != null ||
    ridesOnboard.length > 0 ||
    distance != null ||
    (typeof shiftBand === 'string' && shiftBand.trim() !== '') ||
    (typeof shiftName === 'string' && shiftName.trim() !== '') ||
    (typeof routeName === 'string' && routeName.trim() !== '') ||
    (typeof fleetName === 'string' && fleetName.trim() !== '') ||
    (typeof manifestName === 'string' && manifestName.trim() !== '');
  if (!hasAny) return undefined;
  return {
    vehicle_shift_id: typeof shift === 'string' && shift.trim() !== '' ? shift.trim() : undefined,
    segment_index: seg != null ? Math.floor(seg) : undefined,
    from_driver_activity_number: fromAct != null ? fromAct : undefined,
    to_driver_activity_number: toAct != null ? toAct : undefined,
    rides_onboard_ids: ridesOnboard.length > 0 ? ridesOnboard : undefined,
    distance_m: distance != null ? distance : undefined,
    shift_band: typeof shiftBand === 'string' && shiftBand.trim() !== '' ? shiftBand.trim() : undefined,
    shift_name: typeof shiftName === 'string' && shiftName.trim() !== '' ? shiftName.trim() : undefined,
    route_name: typeof routeName === 'string' && routeName.trim() !== '' ? routeName.trim() : undefined,
    fleet_name: typeof fleetName === 'string' && fleetName.trim() !== '' ? fleetName.trim() : undefined,
    manifest_name: typeof manifestName === 'string' && manifestName.trim() !== '' ? manifestName.trim() : undefined,
  };
}

interface OrderedPathPoint {
  coordinate: [number, number];
  pathOrdinality: number;
  pointOrder: number;
  sequence: number;
}

function extractOrderedPathPoint(
  item: Record<string, unknown>,
  sequence: number
): OrderedPathPoint | null {
  const coordinate = toGeoJsonPoint(item);
  if (coordinate == null) return null;

  return {
    coordinate,
    pathOrdinality: toNumber(item.path_ordinality ?? item.pathOrdinality) ?? 0,
    pointOrder: toNumber(item.point_order ?? item.pointOrder) ?? sequence,
    sequence,
  };
}

function buildLineFromOrderedPoints(points: OrderedPathPoint[]): GeoJsonLineString | null {
  if (points.length < 2) return null;

  const sorted = [...points].sort((a, b) => {
    if (a.pathOrdinality !== b.pathOrdinality) return a.pathOrdinality - b.pathOrdinality;
    if (a.pointOrder !== b.pointOrder) return a.pointOrder - b.pointOrder;
    return a.sequence - b.sequence;
  });

  const coordinates = sorted.map((point) => point.coordinate);
  return coordinates.length > 1 ? { type: 'LineString', coordinates } : null;
}

function extractPathItems(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) {
    return payload.filter((item): item is Record<string, unknown> => item != null && typeof item === 'object');
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;

    for (const key of PATHS_RESPONSE_KEYS) {
      const nested = record[key];
      if (Array.isArray(nested)) {
        return nested.filter((item): item is Record<string, unknown> => item != null && typeof item === 'object');
      }
    }

    const candidate = extractRideId(record);
    if (candidate != null) {
      return [record];
    }

    const mapItems = Object.entries(record)
      .map(([rideId, geometryLike]) => {
        if (typeof rideId !== 'string' || rideId.trim() === '') return null;
        const geometry = toGeoJsonLineString(geometryLike);
        if (geometry == null && (geometryLike == null || typeof geometryLike !== 'object')) return null;
        return {
          ride_id: rideId,
          path: geometryLike,
        } as Record<string, unknown>;
      })
      .filter((item): item is Record<string, unknown> => item != null);

    if (mapItems.length > 0) {
      return mapItems;
    }
  }

  return [];
}

function dedupeUrls(urls: string[]): string[] {
  return Array.from(new Set(urls));
}

async function fetchPathsPayload(
  resultsId: string,
  token: ReturnType<typeof getToken>,
  fetchImpl: typeof fetch,
  signal: AbortSignal
): Promise<unknown | null> {
  const buildCandidateUrls = (baseUrl: string): string[] => {
    const sanitizedBaseUrl = baseUrl.replace(/\/+$/, '');
    return dedupeUrls([
      `${sanitizedBaseUrl}/results/${resultsId}/paths`,
      `${sanitizedBaseUrl}/api/results/${resultsId}/paths`,
    ]);
  };

  const candidateUrls = [
    ...buildCandidateUrls(API_BASE_URL),
    ...(FALLBACK_API_BASE_URL && FALLBACK_API_BASE_URL !== API_BASE_URL
      ? buildCandidateUrls(FALLBACK_API_BASE_URL)
      : []),
  ];

  const headers = {
    'Content-Type': 'application/json',
    ...(token?.access_token ? { Authorization: `Bearer ${token.access_token}` } : {}),
  };

  // Try candidates in order; stop after the first 2xx so we do not spam fallbacks (e.g. localhost 404s)
  // after the simulator already returned paths.
  for (const url of candidateUrls) {
    try {
      const res = await fetchImpl(url, { method: 'GET', headers, signal });
      if (!res.ok) continue;
      return await res.json();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') throw error;
    }
  }
  return null;
}

export async function fetchRoadRoutesByResultsId(
  resultsId: string,
  options: ResultsPathsOptions = {}
): Promise<Record<string, GeoJsonLineString | null>> {
  if (resultsId.trim() === '') return {};

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const token = getToken();

  try {
    const payload = await fetchPathsPayload(resultsId, token, fetchImpl, controller.signal);
    if (!payload) return {};
    const items = extractPathItems(payload);
    const routesByRideId: Record<string, GeoJsonLineString | null> = {};
    const pointRowsByRideId: Record<string, OrderedPathPoint[]> = {};

    items.forEach((item, index) => {
      const primaryRideId = extractPathPointGroupId(item);
      if (primaryRideId == null) return;

      // Index this item under ALL id fields so any caller ID space matches.
      const allIds = extractAllRideIds(item);

      const geometry = extractGeometry(item);
      if (geometry != null) {
        allIds.forEach((id) => { routesByRideId[id] = geometry; });
        return;
      }

      const point = extractOrderedPathPoint(item, index);
      if (point == null) {
        allIds.forEach((id) => { if (!(id in routesByRideId)) routesByRideId[id] = null; });
        return;
      }

      // Accumulate path points under the primary id; aliases are resolved after building.
      if (!pointRowsByRideId[primaryRideId]) {
        pointRowsByRideId[primaryRideId] = [];
      }
      pointRowsByRideId[primaryRideId].push(point);
    });

    Object.entries(pointRowsByRideId).forEach(([rideId, points]) => {
      if (routesByRideId[rideId] != null) return;
      const line = buildLineFromOrderedPoints(points);
      // Publish the built line under every alias for this primary id.
      const representativeItem = items.find((item) => extractPathPointGroupId(item) === rideId);
      const aliases = representativeItem ? extractAllRideIds(representativeItem) : [rideId];
      aliases.forEach((id) => { routesByRideId[id] = line; });
    });

    return routesByRideId;
  } catch {
    return {};
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Module-level cache for road routes by resultsId.
 * Road geometry doesn't change for a given result, so we cache for the session.
 */
const _roadRoutesCache = new Map<string, Record<string, RoadRouteWithMetadata>>();

/**
 * Enriched variant of `/paths` parsing for trip-focused highlighting.
 * Keeps existing aliasing behavior while attaching segment metadata (when present).
 * Results are cached per resultsId for the session — repeat calls return instantly.
 */
export async function fetchRoadRoutesByResultsIdWithMetadata(
  resultsId: string,
  options: ResultsPathsOptions = {}
): Promise<Record<string, RoadRouteWithMetadata>> {
  if (resultsId.trim() === '') return {};

  const cachedResult = _roadRoutesCache.get(resultsId);
  if (cachedResult) return cachedResult;

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const token = getToken();

  try {
    const payload = await fetchPathsPayload(resultsId, token, fetchImpl, controller.signal);
    if (!payload) {
      if (options.throwOnError) throw new Error(`Failed to fetch paths for ${resultsId}`);
      return {};
    }
    const items = extractPathItems(payload);
    const routesById: Record<string, RoadRouteWithMetadata> = {};
    const pointRowsByRideId: Record<string, OrderedPathPoint[]> = {};
    const metadataByPrimaryId: Record<string, SegmentRouteMetadata | undefined> = {};

    items.forEach((item, index) => {
      const primaryRideId = extractPathPointGroupId(item);
      if (primaryRideId == null) return;
      const allIds = extractAllRideIds(item);
      const metadata = extractSegmentRouteMetadata(item);
      metadataByPrimaryId[primaryRideId] = metadata;

      const geometry = extractGeometry(item);
      if (geometry != null) {
        allIds.forEach((id) => {
          routesById[id] = { geometry, metadata };
        });
        return;
      }

      const point = extractOrderedPathPoint(item, index);
      if (point == null) {
        allIds.forEach((id) => {
          if (!(id in routesById)) routesById[id] = { geometry: null, metadata };
        });
        return;
      }
      if (!pointRowsByRideId[primaryRideId]) pointRowsByRideId[primaryRideId] = [];
      pointRowsByRideId[primaryRideId].push(point);
    });

    Object.entries(pointRowsByRideId).forEach(([rideId, points]) => {
      if (routesById[rideId]?.geometry != null) return;
      const line = buildLineFromOrderedPoints(points);
      const representativeItem = items.find((item) => extractPathPointGroupId(item) === rideId);
      const aliases = representativeItem ? extractAllRideIds(representativeItem) : [rideId];
      const metadata = metadataByPrimaryId[rideId];
      aliases.forEach((id) => {
        routesById[id] = { geometry: line, metadata };
      });
    });

    _roadRoutesCache.set(resultsId, routesById);
    return routesById;
  } catch (error) {
    if (options.throwOnError) throw error;
    return {};
  } finally {
    clearTimeout(timeoutId);
  }
}
